import type { TFunction } from "i18next";
import { isProblemDetails } from "@qorakul/shared/api";

const SECONDS_PER_MINUTE = 60;
/** Waits up to this long are shown in seconds; longer ones in whole minutes, rounded up. */
const LONGEST_WAIT_IN_SECONDS = 119;
const DELTA_SECONDS = /^\d+$/;

/**
 * How long the API asked the client to wait, for 423 (lockout) and 429 (rate limit) answers: the
 * `retryAfterSeconds` problem-details extension when present, otherwise the `Retry-After` header (delta seconds).
 */
export function retryAfterSeconds(error: unknown, headers?: Headers): number | undefined {
  const fromBody = isProblemDetails(error) ? error["retryAfterSeconds"] : undefined;
  if (typeof fromBody === "number" && Number.isFinite(fromBody) && fromBody >= 0) return Math.ceil(fromBody);
  const fromHeader = headers?.get("Retry-After")?.trim();
  return fromHeader !== undefined && DELTA_SECONDS.test(fromHeader) ? Number(fromHeader) : undefined;
}

/** "45 soniyadan" or "15 daqiqadan", to be placed before "keyin". */
function waitText(t: TFunction, seconds: number): string {
  if (seconds <= LONGEST_WAIT_IN_SECONDS) return t("auth.waitSeconds", { value: seconds });
  return t("auth.waitMinutes", { value: Math.ceil(seconds / SECONDS_PER_MINUTE) });
}

/** Message for a 429 answer; without a known wait it still tells the user to try again later. */
export function rateLimitedMessage(t: TFunction, seconds: number | undefined): string {
  return seconds === undefined ? t("auth.rateLimitedNoWait") : t("auth.rateLimited", { wait: waitText(t, seconds) });
}

/** Message for a 423 answer (account locked after repeated wrong passwords). */
export function lockedMessage(t: TFunction, seconds: number | undefined): string {
  return t("auth.locked", { wait: waitText(t, seconds ?? 0) });
}
